import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { pl } from 'date-fns/locale';
import type { TFunction } from 'i18next';
import { X } from 'lucide-react';
import { Controller, useController, useForm, type Control, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UpdateOfferSchema } from '@ooh/contracts';
import type { Offer, UpdateOffer } from '@ooh/contracts';
import { formatDateValue, parseOptionalDateInputValue } from '@ooh/ui/lib/date-input-helpers';
import { CampaignDurationField as SharedCampaignDurationField } from '@ooh/screens';
import { BRANDING_MODE_OPTIONS } from '../lib/offer-constants';
import { Button } from '@ooh/ui/components/button';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { DatePickerButton } from '@ooh/ui/components/date-picker-button';
import { Input } from '@ooh/ui/components/input';
import { Label } from '@ooh/ui/components/label';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import { Textarea } from '@ooh/ui/components/textarea';

type OfferDrawerFormValues = UpdateOffer;

interface EditDrawerProps {
  clientBrands?: string[];
  isOpen: boolean;
  isPending?: boolean;
  offer: Offer;
  onClose: () => void;
  onSubmit: (values: OfferDrawerFormValues) => Promise<void>;
  resetToken?: number;
}

function getBrandOptions(clientBrands: string[] | undefined, currentBrand: string | null) {
  const values = new Set(clientBrands ?? []);
  if (currentBrand) {
    values.add(currentBrand);
  }

  return [...values].map((brand) => ({ label: brand, value: brand }));
}

function normalizeNotificationEmails(value: Offer['notificationEmails']): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
}

function normalizePriceColumnsConfig(value: Offer['priceColumnsConfig']): unknown[] {
  return Array.isArray(value) ? value : [];
}

function getDefaultValues(offer: Offer): OfferDrawerFormValues {
  return {
    brand: offer.brand ?? null,
    brandingMode: offer.brandingMode,
    defaultDateFrom: offer.defaultDateFrom,
    defaultDateTo: offer.defaultDateTo,
    defaultPeriodGranularity: offer.defaultPeriodGranularity ?? undefined,
    notificationEmails: normalizeNotificationEmails(offer.notificationEmails),
    priceColumnsConfig: normalizePriceColumnsConfig(offer.priceColumnsConfig),
    title: offer.title,
    updatedAt: offer.updatedAt,
    validUntil: offer.validUntil,
  };
}

interface CampaignDurationFieldProps {
  control: Control<OfferDrawerFormValues>;
  t: TFunction<'offers'>;
}

/**
 * Composes the separately-tracked `defaultDateFrom`/`defaultDateTo` RHF
 * fields into a single `DateRangeButtonField` trigger. Kept as its own
 * component (rather than two `Controller`s inline in `EditDrawer`) so both
 * fields' `useController` subscriptions — which the single combined trigger
 * needs simultaneously — live together in one dedicated place instead of
 * subscribing the whole drawer render function to both.
 */
function CampaignDurationField({ control, t }: CampaignDurationFieldProps) {
  const { field: fromField } = useController({ control, name: 'defaultDateFrom' });
  const { field: toField } = useController({ control, name: 'defaultDateTo' });

  return (
    <SharedCampaignDurationField
      id="edit-offer-campaign-duration-trigger"
      fieldTestId="edit-offer-campaign-duration"
      label={t('workspace.header.fields.campaignDuration')}
      placeholder={t('workspace.header.fields.dateRangePlaceholder')}
      fromValue={fromField.value}
      toValue={toField.value}
      onChange={({ from, to }) => {
        fromField.onChange(from);
        toField.onChange(to);
      }}
    />
  );
}

export function EditDrawer({
  clientBrands,
  isOpen,
  isPending = false,
  offer,
  onClose,
  onSubmit,
  resetToken = 0,
}: EditDrawerProps) {
  const { t } = useTranslation('offers');
  const [publishWithoutPrices, setPublishWithoutPrices] = React.useState(false);
  // Separate display string for priceColumnsConfig so the textarea stays editable
  // while the user is typing potentially-invalid JSON.
  const [priceColumnsDisplay, setPriceColumnsDisplay] = React.useState(() =>
    JSON.stringify(normalizePriceColumnsConfig(offer.priceColumnsConfig)),
  );
  const form = useForm<OfferDrawerFormValues>({
    defaultValues: getDefaultValues(offer),
    resolver: zodResolver(UpdateOfferSchema) as Resolver<OfferDrawerFormValues>,
  });

  React.useEffect(() => {
    form.reset(getDefaultValues(offer));
    setPriceColumnsDisplay(JSON.stringify(normalizePriceColumnsConfig(offer.priceColumnsConfig)));
    setPublishWithoutPrices(false);
  }, [form, offer, resetToken]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="offer-header-edit-drawer-root"
    >
      <div aria-hidden="true" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        aria-labelledby="offer-header-edit-drawer-title"
        className="relative z-10 flex h-full w-full max-w-2xl flex-col border-l border-border bg-background shadow-xl"
        data-testid="offer-header-edit-drawer"
        role="dialog"
      >
        <div className="flex items-start justify-between border-b border-border/50 px-6 py-4">
          <h2 className="text-base font-semibold" id="offer-header-edit-drawer-title">
            {t('workspace.header.drawer.title')}
          </h2>
          <Button
            aria-label={t('workspace.header.drawer.close')}
            type="button"
            variant="ghost"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid flex-1 gap-4 overflow-y-auto px-6 py-5 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="edit-offer-title">{t('workspace.header.fields.title')}</Label>
            <Input
              id="edit-offer-title"
              aria-label={t('workspace.header.fields.title')}
              {...form.register('title')}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('workspace.header.fields.brandingMode')}</Label>
            <Controller
              control={form.control}
              name="brandingMode"
              render={({ field }) => (
                <SearchableSelectField
                  data-testid="branding-mode"
                  options={BRANDING_MODE_OPTIONS.map((option) => ({
                    label: t(option.labelKey),
                    value: option.value,
                  }))}
                  value={typeof field.value === 'string' ? field.value : ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('workspace.header.fields.brand')}</Label>
            <Controller
              control={form.control}
              name="brand"
              render={({ field }) => (
                <SearchableSelectField
                  data-testid="offer-brand"
                  options={getBrandOptions(clientBrands, offer.brand)}
                  placeholder={t('workspace.header.options.noBrand')}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value === '' ? null : value)}
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-offer-valid-until">
              {t('workspace.header.fields.validUntil')}
            </Label>
            <Controller
              control={form.control}
              name="validUntil"
              render={({ field }) => (
                <DatePickerButton
                  id="edit-offer-valid-until"
                  aria-label={t('workspace.header.fields.validUntil')}
                  testId="edit-offer-valid-until"
                  placeholder={t('workspace.header.fields.datePlaceholder')}
                  locale={pl}
                  required
                  value={formatDateValue(field.value)}
                  onChange={(next) => field.onChange(parseOptionalDateInputValue(next))}
                />
              )}
            />
          </div>

          {/* Grouped into a single grid cell (not two) so it lands in the same row as the
              validUntil field above via the parent grid's auto-flow, instead of the
              defaultDateTo field dangling alone on the next row next to an unrelated field —
              the confusing layout/label pairing flagged in CU-86caypxab. */}
          <CampaignDurationField control={form.control} t={t} />

          <div className="space-y-1.5">
            <Label>{t('workspace.header.fields.defaultPeriodGranularity')}</Label>
            <Controller
              control={form.control}
              name="defaultPeriodGranularity"
              render={({ field }) => (
                <SearchableSelectField
                  data-testid="default-period-granularity"
                  options={[
                    {
                      label: t('workspace.header.options.periodGranularity.halfMonth'),
                      value: 'HALF_MONTH',
                    },
                    {
                      label: t('workspace.header.options.periodGranularity.month'),
                      value: 'MONTH',
                    },
                    { label: t('workspace.header.options.periodGranularity.week'), value: 'WEEK' },
                  ]}
                  value={typeof field.value === 'string' ? field.value : ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="edit-offer-emails">
              {t('workspace.header.fields.notificationEmails')}
            </Label>
            <Controller
              control={form.control}
              name="notificationEmails"
              render={({ field }) => (
                <Textarea
                  id="edit-offer-emails"
                  aria-label={t('workspace.header.fields.notificationEmails')}
                  value={normalizeNotificationEmails(field.value).join(', ')}
                  onChange={(event) => {
                    const values = event.target.value
                      .split(',')
                      .map((item) => item.trim())
                      .filter(Boolean);
                    field.onChange(values);
                  }}
                />
              )}
            />
          </div>

          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="edit-offer-price-columns">
              {t('workspace.header.fields.priceColumnsConfig')}
            </Label>
            <Controller
              control={form.control}
              name="priceColumnsConfig"
              render={({ field }) => (
                <Textarea
                  id="edit-offer-price-columns"
                  aria-label={t('workspace.header.fields.priceColumnsConfig')}
                  value={priceColumnsDisplay}
                  onChange={(event) => {
                    setPriceColumnsDisplay(event.target.value);
                    try {
                      // Commit to the form field only when the JSON is valid.
                      field.onChange(JSON.parse(event.target.value));
                    } catch {
                      // Invalid JSON — keep display string editable but don't update field.
                    }
                  }}
                />
              )}
            />
          </div>

          <div className="flex items-center gap-3 md:col-span-2">
            <Checkbox
              checked={publishWithoutPrices}
              id="publish-without-prices"
              onCheckedChange={(checked) => setPublishWithoutPrices(checked === true)}
            />
            <Label htmlFor="publish-without-prices">
              {t('workspace.header.fields.publishWithoutPrices')}
            </Label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border/50 px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('workspace.header.drawer.cancel')}
          </Button>
          <Button
            data-testid="offer-header-edit-save"
            disabled={isPending}
            type="button"
            onClick={() => void form.handleSubmit(onSubmit)()}
          >
            {t('workspace.header.drawer.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
